import React, { useState, useEffect, useRef } from "react";
import { searchAccounts, searchLocations } from "./api";

const DEBOUNCE_MS = 300;

const useDebouncedSearch = (search, active) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    clearTimeout(timerRef.current);

    if (!active || !query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    timerRef.current = setTimeout(() => {
      search(query)
        .then((items) => {
          if (requestIdRef.current === requestId) setResults(items);
        })
        .catch(() => {
          if (requestIdRef.current === requestId) setResults([]);
        })
        .finally(() => {
          if (requestIdRef.current === requestId) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, active]);

  return { query, setQuery, results, setResults, loading };
};

const SearchSelect = ({ label, placeholder, disabled, disabledReason, selected, onSelect, onClear, search }) => {
  const { query, setQuery, results, setResults, loading } = useDebouncedSearch(search, !disabled);
  const [open, setOpen] = useState(false);

  const handlePick = (item) => {
    onSelect(item);
    setOpen(false);
    setResults([]);
    setQuery("");
  };

  if (selected) {
    return (
      <div className="mb-2">
        <div className="small text-muted mb-1">{label}</div>
        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
          <span className="badge bg-light text-dark border">{selected.name}</span>
          <button type="button" className="btn btn-sm btn-link p-0" onClick={onClear}>
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2" style={{ position: "relative" }}>
      <label className="form-label small mb-1">{label}</label>
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder={placeholder}
        value={query}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {disabled && disabledReason && <div className="form-text">{disabledReason}</div>}
      {open && !disabled && (loading || results.length > 0) && (
        <ul
          className="list-group position-absolute w-100"
          style={{ zIndex: 10, maxHeight: "200px", overflowY: "auto" }}
        >
          {loading && <li className="list-group-item small text-muted py-1">Searching...</li>}
          {!loading && results.length === 0 && (
            <li className="list-group-item small text-muted py-1">No matches</li>
          )}
          {!loading &&
            results.map((item) => (
              <li
                key={item.id}
                className="list-group-item list-group-item-action small py-1"
                style={{ cursor: "pointer" }}
                onMouseDown={() => handlePick(item)}
              >
                {item.name}
                {item.platform && <span className="text-muted"> ({item.platform})</span>}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

// The playground's target picker: which Account (and, within it, which
// Location / R365Location) Step 1's Chouxbox source pulls from and Steps 5-6
// scope vendor + item matching against. Platform
// (chouxbox vs r365) is derived from the chosen account, not picked
// separately, since it's already stored on Account and determines which
// Synthesize class map_data should run.
const AccountLocationPicker = ({ account, location, onAccountChange, onLocationChange }) => {
  return (
    <div className="card" style={{ minWidth: "260px", maxWidth: "320px" }}>
      <div className="card-body">
        <h6 className="card-title">Target account</h6>
        <p className="card-text small text-muted mb-2">
          Where a Chouxbox upload is pulled from (step 1), and the account/location steps 5-6
          match and validate against.
        </p>

        <SearchSelect
          label="Account"
          placeholder="Search accounts..."
          disabled={false}
          selected={account}
          onSelect={onAccountChange}
          onClear={() => onAccountChange(null)}
          search={(q) => searchAccounts(q).then((d) => d.accounts)}
        />

        <SearchSelect
          label={account && account.platform === "r365" ? "R365 Location" : "Location"}
          placeholder="Search locations..."
          disabled={!account}
          disabledReason="Choose an account first."
          selected={location}
          onSelect={onLocationChange}
          onClear={() => onLocationChange(null)}
          search={(q) => (account ? searchLocations(account.id, q).then((d) => d.locations) : Promise.resolve([]))}
        />
      </div>
    </div>
  );
};

export default AccountLocationPicker;
